import isNil from "lodash/isNil";
import { useEffect, useMemo, useState } from "react";
import isNull from "lodash/isNull";

import { useOperator, useOperatorCurrency } from "globals/hooks";
import { CustomerPricingType } from "globals/hooks/utils";
import { AutoPaymentPreference } from "types";

const { NoCharge, FullCharge, Deposit } = AutoPaymentPreference;

type UseAutoChargePaymentType = {
  pricings: CustomerPricingType[];
  skip?: boolean;
};

function useAutoChargePayment({ pricings, skip }: UseAutoChargePaymentType) {
  // hooks
  const { operator } = useOperator();
  const currency = useOperatorCurrency();

  // state
  const [autoPaymentAmt, setAutoPaymentAmt] = useState(null);

  // derived state
  const orderTotalAmt = pricings.reduce(
    (orderTotalAmt, pricing) => pricing.orderTotalAmt + orderTotalAmt,
    0
  );

  const roundedOrderTotalAmt = Math.round(orderTotalAmt * 100) / 100;

  const {
    settings: {
      autoPaymentDeposit: { amt, percent } = {},
      autoPaymentPreference,
    } = {},
    enableCreditCardWhenBooking,
  } = operator;

  // effects
  useEffect(() => {
    if (
      skip ||
      !enableCreditCardWhenBooking ||
      autoPaymentPreference === NoCharge ||
      (autoPaymentPreference === Deposit && (amt === 0 || percent === 0))
    ) {
      return;
    }

    if (
      autoPaymentPreference === FullCharge ||
      (autoPaymentPreference === Deposit && percent === 100)
    ) {
      setAutoPaymentAmt(roundedOrderTotalAmt);
    } else if (!isNil(amt)) {
      amt > roundedOrderTotalAmt
        ? setAutoPaymentAmt(roundedOrderTotalAmt)
        : setAutoPaymentAmt(amt);
    } else {
      // Convert to cents, apply percentage, round, then convert back to dollars
      // This matches the backend calculation in createAutoPayment.ts
      const orderTotalInCents = Math.round(roundedOrderTotalAmt * 100);
      const depositInCents = Math.round(percent * orderTotalInCents);
      const depositInDollars = depositInCents / 100;
      setAutoPaymentAmt(depositInDollars);
    }
  }, [
    amt,
    autoPaymentPreference,
    enableCreditCardWhenBooking,
    percent,
    roundedOrderTotalAmt,
    skip,
  ]);

  const autoChargeButtonText = useMemo(() => {
    if (!autoPaymentAmt) return null;

    return autoPaymentAmt === roundedOrderTotalAmt
      ? `Pay ${currency(roundedOrderTotalAmt)}`
      : `Pay Deposit: ${currency(autoPaymentAmt)}`;
  }, [autoPaymentAmt, roundedOrderTotalAmt, currency]);

  const autoPaymentType = isNull(autoPaymentAmt)
    ? null
    : autoPaymentAmt === roundedOrderTotalAmt
    ? "full"
    : "partial";

  return {
    autoPaymentAmt,
    autoChargeButtonText,
    autoPaymentType,
  };
}

export { useAutoChargePayment };
